import React, { useState, useEffect } from 'react';
import { ArrowLeft, ChevronRight, Gamepad2, RotateCcw, AlertTriangle, Cpu, Zap, Monitor, MemoryStick } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../LanguageContext';

const FPSEstimatorPage = () => {
  const { language } = useLanguage();
  
  const [step, setStep] = useState(1);
  const [gpuBrand, setGpuBrand] = useState(null);
  const [cpuBrand, setCpuBrand] = useState(null);
  const [selectedGpu, setSelectedGpu] = useState(null);
  const [selectedCpu, setSelectedCpu] = useState(null);
  const [ramType, setRamType] = useState('DDR5');
  const [ramSize, setRamSize] = useState(16);
  const [ramFreq, setRamFreq] = useState(6000);
  const [showResults, setShowResults] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [animatedGains, setAnimatedGains] = useState({ avg: 0, low1: 0, peak: 0 });

  // GPU Data - COMPLETE LIST
  const gpuData = {
    nvidia: {
      name: 'GeForce',
      logo: 'NVIDIA',
      items: [
        // RTX 50 Series (2025)
        { name: 'RTX 5090', tier: 'flagship', score: 100 },
        { name: 'RTX 5090D', tier: 'flagship', score: 98 },
        { name: 'RTX 5080', tier: 'flagship', score: 92 },
        { name: 'RTX 5070 Ti', tier: 'high', score: 85 },
        { name: 'RTX 5070', tier: 'high', score: 80 },
        { name: 'RTX 5060 Ti', tier: 'mid', score: 70 },
        { name: 'RTX 5060', tier: 'mid', score: 65 },
        // RTX 40 Series
        { name: 'RTX 4090', tier: 'flagship', score: 95 },
        { name: 'RTX 4090D', tier: 'flagship', score: 93 },
        { name: 'RTX 4080 Super', tier: 'flagship', score: 88 },
        { name: 'RTX 4080', tier: 'high', score: 85 },
        { name: 'RTX 4070 Ti Super', tier: 'high', score: 80 },
        { name: 'RTX 4070 Ti', tier: 'high', score: 77 },
        { name: 'RTX 4070 Super', tier: 'high', score: 74 },
        { name: 'RTX 4070', tier: 'mid', score: 70 },
        { name: 'RTX 4060 Ti 16GB', tier: 'mid', score: 64 },
        { name: 'RTX 4060 Ti 8GB', tier: 'mid', score: 62 },
        { name: 'RTX 4060', tier: 'mid', score: 55 },
        // RTX 30 Series
        { name: 'RTX 3090 Ti', tier: 'high', score: 78 },
        { name: 'RTX 3090', tier: 'high', score: 75 },
        { name: 'RTX 3080 Ti', tier: 'high', score: 72 },
        { name: 'RTX 3080 12GB', tier: 'mid', score: 70 },
        { name: 'RTX 3080 10GB', tier: 'mid', score: 68 },
        { name: 'RTX 3070 Ti', tier: 'mid', score: 60 },
        { name: 'RTX 3070', tier: 'mid', score: 55 },
        { name: 'RTX 3060 Ti', tier: 'entry', score: 48 },
        { name: 'RTX 3060 12GB', tier: 'entry', score: 42 },
        { name: 'RTX 3050 8GB', tier: 'entry', score: 32 },
        { name: 'RTX 3050 6GB', tier: 'entry', score: 28 },
        // RTX 20 Series
        { name: 'RTX 2080 Ti', tier: 'mid', score: 55 },
        { name: 'RTX 2080 Super', tier: 'mid', score: 50 },
        { name: 'RTX 2080', tier: 'mid', score: 48 },
        { name: 'RTX 2070 Super', tier: 'entry', score: 45 },
        { name: 'RTX 2070', tier: 'entry', score: 42 },
        { name: 'RTX 2060 Super', tier: 'entry', score: 38 },
        { name: 'RTX 2060 12GB', tier: 'entry', score: 36 },
        { name: 'RTX 2060 6GB', tier: 'entry', score: 34 },
        // GTX 16 Series
        { name: 'GTX 1660 Ti', tier: 'entry', score: 32 },
        { name: 'GTX 1660 Super', tier: 'entry', score: 30 },
        { name: 'GTX 1660', tier: 'entry', score: 28 },
        { name: 'GTX 1650 Super', tier: 'entry', score: 25 },
        { name: 'GTX 1650 GDDR6', tier: 'entry', score: 23 },
        { name: 'GTX 1650 GDDR5', tier: 'entry', score: 20 },
        // GTX 10 Series
        { name: 'GTX 1080 Ti', tier: 'entry', score: 40 },
        { name: 'GTX 1080', tier: 'entry', score: 35 },
        { name: 'GTX 1070 Ti', tier: 'entry', score: 32 },
        { name: 'GTX 1070', tier: 'entry', score: 30 },
        { name: 'GTX 1060 6GB', tier: 'entry', score: 22 },
        { name: 'GTX 1060 3GB', tier: 'entry', score: 18 },
        { name: 'GTX 1050 Ti', tier: 'entry', score: 15 },
        { name: 'GTX 1050', tier: 'entry', score: 12 },
      ]
    },
    amd: {
      name: 'Radeon',
      logo: 'AMD',
      items: [
        // RX 9000 Series (2025)
        { name: 'RX 9070 XT', tier: 'flagship', score: 88 },
        { name: 'RX 9070', tier: 'high', score: 82 },
        // RX 7000 Series
        { name: 'RX 7900 XTX', tier: 'flagship', score: 90 },
        { name: 'RX 7900 XT', tier: 'flagship', score: 85 },
        { name: 'RX 7900 GRE', tier: 'high', score: 78 },
        { name: 'RX 7800 XT', tier: 'high', score: 72 },
        { name: 'RX 7700 XT', tier: 'mid', score: 65 },
        { name: 'RX 7600 XT', tier: 'mid', score: 55 },
        { name: 'RX 7600', tier: 'mid', score: 50 },
        // RX 6000 Series
        { name: 'RX 6950 XT', tier: 'high', score: 75 },
        { name: 'RX 6900 XT', tier: 'high', score: 72 },
        { name: 'RX 6800 XT', tier: 'high', score: 68 },
        { name: 'RX 6800', tier: 'mid', score: 62 },
        { name: 'RX 6750 XT', tier: 'mid', score: 55 },
        { name: 'RX 6700 XT', tier: 'mid', score: 50 },
        { name: 'RX 6700 10GB', tier: 'mid', score: 45 },
        { name: 'RX 6650 XT', tier: 'entry', score: 42 },
        { name: 'RX 6600 XT', tier: 'entry', score: 40 },
        { name: 'RX 6600', tier: 'entry', score: 35 },
        { name: 'RX 6500 XT', tier: 'entry', score: 25 },
        { name: 'RX 6400', tier: 'entry', score: 20 },
        // RX 5000 Series
        { name: 'RX 5700 XT', tier: 'entry', score: 45 },
        { name: 'RX 5700', tier: 'entry', score: 42 },
        { name: 'RX 5600 XT', tier: 'entry', score: 38 },
        { name: 'RX 5500 XT 8GB', tier: 'entry', score: 28 },
        { name: 'RX 5500 XT 4GB', tier: 'entry', score: 24 },
        // RX 500/400 Series
        { name: 'RX 590', tier: 'entry', score: 25 },
        { name: 'RX 580 8GB', tier: 'entry', score: 22 },
        { name: 'RX 580 4GB', tier: 'entry', score: 18 },
        { name: 'RX 570 8GB', tier: 'entry', score: 18 },
        { name: 'RX 570 4GB', tier: 'entry', score: 15 },
        { name: 'RX 560', tier: 'entry', score: 12 },
        { name: 'RX 550', tier: 'entry', score: 8 },
      ]
    }
  };

  // CPU Data - COMPLETE LIST
  const cpuData = {
    intel: {
      name: 'Core',
      logo: 'Intel',
      items: [
        // Arrow Lake (LGA 1851) - 2024/2025
        { name: 'Core Ultra 9 285K', tier: 'flagship', score: 100, socket: 'LGA 1851' },
        { name: 'Core Ultra 9 285', tier: 'flagship', score: 98, socket: 'LGA 1851' },
        { name: 'Core Ultra 7 265K', tier: 'flagship', score: 95, socket: 'LGA 1851' },
        { name: 'Core Ultra 7 265KF', tier: 'flagship', score: 94, socket: 'LGA 1851' },
        { name: 'Core Ultra 7 265', tier: 'high', score: 92, socket: 'LGA 1851' },
        { name: 'Core Ultra 5 245K', tier: 'high', score: 88, socket: 'LGA 1851' },
        { name: 'Core Ultra 5 245KF', tier: 'high', score: 87, socket: 'LGA 1851' },
        { name: 'Core Ultra 5 245', tier: 'high', score: 85, socket: 'LGA 1851' },
        // Raptor Lake Refresh (14th Gen)
        { name: 'Core i9-14900KS', tier: 'flagship', score: 98, socket: 'LGA 1700' },
        { name: 'Core i9-14900K', tier: 'flagship', score: 96, socket: 'LGA 1700' },
        { name: 'Core i9-14900KF', tier: 'flagship', score: 95, socket: 'LGA 1700' },
        { name: 'Core i9-14900', tier: 'high', score: 92, socket: 'LGA 1700' },
        { name: 'Core i9-14900F', tier: 'high', score: 91, socket: 'LGA 1700' },
        { name: 'Core i7-14700K', tier: 'high', score: 90, socket: 'LGA 1700' },
        { name: 'Core i7-14700KF', tier: 'high', score: 89, socket: 'LGA 1700' },
        { name: 'Core i7-14700', tier: 'high', score: 86, socket: 'LGA 1700' },
        { name: 'Core i7-14700F', tier: 'high', score: 85, socket: 'LGA 1700' },
        { name: 'Core i5-14600K', tier: 'high', score: 82, socket: 'LGA 1700' },
        { name: 'Core i5-14600KF', tier: 'high', score: 81, socket: 'LGA 1700' },
        { name: 'Core i5-14600', tier: 'mid', score: 78, socket: 'LGA 1700' },
        { name: 'Core i5-14500', tier: 'mid', score: 75, socket: 'LGA 1700' },
        { name: 'Core i5-14400', tier: 'mid', score: 72, socket: 'LGA 1700' },
        { name: 'Core i5-14400F', tier: 'mid', score: 71, socket: 'LGA 1700' },
        { name: 'Core i3-14100', tier: 'entry', score: 55, socket: 'LGA 1700' },
        { name: 'Core i3-14100F', tier: 'entry', score: 54, socket: 'LGA 1700' },
        // Raptor Lake (13th Gen)
        { name: 'Core i9-13900KS', tier: 'flagship', score: 94, socket: 'LGA 1700' },
        { name: 'Core i9-13900K', tier: 'flagship', score: 92, socket: 'LGA 1700' },
        { name: 'Core i9-13900KF', tier: 'flagship', score: 91, socket: 'LGA 1700' },
        { name: 'Core i9-13900', tier: 'high', score: 88, socket: 'LGA 1700' },
        { name: 'Core i9-13900F', tier: 'high', score: 87, socket: 'LGA 1700' },
        { name: 'Core i7-13700K', tier: 'high', score: 85, socket: 'LGA 1700' },
        { name: 'Core i7-13700KF', tier: 'high', score: 84, socket: 'LGA 1700' },
        { name: 'Core i7-13700', tier: 'high', score: 82, socket: 'LGA 1700' },
        { name: 'Core i7-13700F', tier: 'high', score: 81, socket: 'LGA 1700' },
        { name: 'Core i5-13600K', tier: 'mid', score: 78, socket: 'LGA 1700' },
        { name: 'Core i5-13600KF', tier: 'mid', score: 77, socket: 'LGA 1700' },
        { name: 'Core i5-13500', tier: 'mid', score: 72, socket: 'LGA 1700' },
        { name: 'Core i5-13400', tier: 'mid', score: 68, socket: 'LGA 1700' },
        { name: 'Core i5-13400F', tier: 'mid', score: 67, socket: 'LGA 1700' },
        { name: 'Core i3-13100', tier: 'entry', score: 52, socket: 'LGA 1700' },
        { name: 'Core i3-13100F', tier: 'entry', score: 51, socket: 'LGA 1700' },
        // Alder Lake (12th Gen)
        { name: 'Core i9-12900KS', tier: 'high', score: 85, socket: 'LGA 1700' },
        { name: 'Core i9-12900K', tier: 'high', score: 82, socket: 'LGA 1700' },
        { name: 'Core i9-12900KF', tier: 'high', score: 81, socket: 'LGA 1700' },
        { name: 'Core i9-12900', tier: 'mid', score: 78, socket: 'LGA 1700' },
        { name: 'Core i9-12900F', tier: 'mid', score: 77, socket: 'LGA 1700' },
        { name: 'Core i7-12700K', tier: 'mid', score: 75, socket: 'LGA 1700' },
        { name: 'Core i7-12700KF', tier: 'mid', score: 74, socket: 'LGA 1700' },
        { name: 'Core i7-12700', tier: 'mid', score: 72, socket: 'LGA 1700' },
        { name: 'Core i7-12700F', tier: 'mid', score: 71, socket: 'LGA 1700' },
        { name: 'Core i5-12600K', tier: 'mid', score: 68, socket: 'LGA 1700' },
        { name: 'Core i5-12600KF', tier: 'mid', score: 67, socket: 'LGA 1700' },
        { name: 'Core i5-12500', tier: 'entry', score: 62, socket: 'LGA 1700' },
        { name: 'Core i5-12400', tier: 'entry', score: 58, socket: 'LGA 1700' },
        { name: 'Core i5-12400F', tier: 'entry', score: 57, socket: 'LGA 1700' },
        { name: 'Core i3-12100', tier: 'entry', score: 48, socket: 'LGA 1700' },
        { name: 'Core i3-12100F', tier: 'entry', score: 47, socket: 'LGA 1700' },
        // Older Gens
        { name: 'Core i9-11900K', tier: 'entry', score: 65, socket: 'LGA 1200' },
        { name: 'Core i7-11700K', tier: 'entry', score: 58, socket: 'LGA 1200' },
        { name: 'Core i5-11600K', tier: 'entry', score: 52, socket: 'LGA 1200' },
        { name: 'Core i9-10900K', tier: 'entry', score: 60, socket: 'LGA 1200' },
        { name: 'Core i7-10700K', tier: 'entry', score: 55, socket: 'LGA 1200' },
        { name: 'Core i5-10600K', tier: 'entry', score: 48, socket: 'LGA 1200' },
      ]
    },
    amd: {
      name: 'Ryzen',
      logo: 'AMD',
      items: [
        // Ryzen 9000 Series (Zen 5 - AM5)
        { name: 'Ryzen 9 9950X', tier: 'flagship', score: 98, socket: 'AM5' },
        { name: 'Ryzen 9 9900X', tier: 'flagship', score: 95, socket: 'AM5' },
        { name: 'Ryzen 7 9800X3D', tier: 'flagship', score: 100, socket: 'AM5' },
        { name: 'Ryzen 7 9700X', tier: 'high', score: 88, socket: 'AM5' },
        { name: 'Ryzen 5 9600X', tier: 'high', score: 82, socket: 'AM5' },
        // Ryzen 8000G Series (APU - AM5)
        { name: 'Ryzen 9 8945HS', tier: 'mid', score: 72, socket: 'AM5' },
        { name: 'Ryzen 7 8700G', tier: 'mid', score: 70, socket: 'AM5' },
        { name: 'Ryzen 7 8700F', tier: 'mid', score: 72, socket: 'AM5' },
        { name: 'Ryzen 5 8600G', tier: 'mid', score: 65, socket: 'AM5' },
        { name: 'Ryzen 5 8500G', tier: 'entry', score: 58, socket: 'AM5' },
        { name: 'Ryzen 5 8400F', tier: 'entry', score: 60, socket: 'AM5' },
        // Ryzen 7000 Series (Zen 4 - AM5)
        { name: 'Ryzen 9 7950X3D', tier: 'flagship', score: 97, socket: 'AM5' },
        { name: 'Ryzen 9 7950X', tier: 'flagship', score: 94, socket: 'AM5' },
        { name: 'Ryzen 9 7900X3D', tier: 'flagship', score: 93, socket: 'AM5' },
        { name: 'Ryzen 9 7900X', tier: 'high', score: 90, socket: 'AM5' },
        { name: 'Ryzen 9 7900', tier: 'high', score: 88, socket: 'AM5' },
        { name: 'Ryzen 7 7800X3D', tier: 'flagship', score: 96, socket: 'AM5' },
        { name: 'Ryzen 7 7700X', tier: 'high', score: 85, socket: 'AM5' },
        { name: 'Ryzen 7 7700', tier: 'high', score: 82, socket: 'AM5' },
        { name: 'Ryzen 5 7600X', tier: 'high', score: 78, socket: 'AM5' },
        { name: 'Ryzen 5 7600', tier: 'mid', score: 75, socket: 'AM5' },
        { name: 'Ryzen 5 7500F', tier: 'mid', score: 72, socket: 'AM5' },
        // Ryzen 5000 Series (Zen 3 - AM4)
        { name: 'Ryzen 9 5950X', tier: 'high', score: 82, socket: 'AM4' },
        { name: 'Ryzen 9 5900X', tier: 'high', score: 80, socket: 'AM4' },
        { name: 'Ryzen 9 5900', tier: 'mid', score: 78, socket: 'AM4' },
        { name: 'Ryzen 7 5800X3D', tier: 'high', score: 88, socket: 'AM4' },
        { name: 'Ryzen 7 5800X', tier: 'mid', score: 75, socket: 'AM4' },
        { name: 'Ryzen 7 5800', tier: 'mid', score: 73, socket: 'AM4' },
        { name: 'Ryzen 7 5700X', tier: 'mid', score: 70, socket: 'AM4' },
        { name: 'Ryzen 7 5700G', tier: 'mid', score: 65, socket: 'AM4' },
        { name: 'Ryzen 7 5700', tier: 'mid', score: 68, socket: 'AM4' },
        { name: 'Ryzen 5 5600X', tier: 'mid', score: 68, socket: 'AM4' },
        { name: 'Ryzen 5 5600', tier: 'entry', score: 65, socket: 'AM4' },
        { name: 'Ryzen 5 5600G', tier: 'entry', score: 58, socket: 'AM4' },
        { name: 'Ryzen 5 5500', tier: 'entry', score: 55, socket: 'AM4' },
        { name: 'Ryzen 5 5500GT', tier: 'entry', score: 52, socket: 'AM4' },
        { name: 'Ryzen 3 5300G', tier: 'entry', score: 45, socket: 'AM4' },
        // Ryzen 3000 Series (Zen 2 - AM4)
        { name: 'Ryzen 9 3950X', tier: 'mid', score: 70, socket: 'AM4' },
        { name: 'Ryzen 9 3900X', tier: 'mid', score: 65, socket: 'AM4' },
        { name: 'Ryzen 9 3900', tier: 'entry', score: 62, socket: 'AM4' },
        { name: 'Ryzen 7 3800X', tier: 'entry', score: 58, socket: 'AM4' },
        { name: 'Ryzen 7 3700X', tier: 'entry', score: 55, socket: 'AM4' },
        { name: 'Ryzen 5 3600X', tier: 'entry', score: 50, socket: 'AM4' },
        { name: 'Ryzen 5 3600', tier: 'entry', score: 48, socket: 'AM4' },
        { name: 'Ryzen 5 3500', tier: 'entry', score: 42, socket: 'AM4' },
        { name: 'Ryzen 3 3300X', tier: 'entry', score: 40, socket: 'AM4' },
        { name: 'Ryzen 3 3100', tier: 'entry', score: 35, socket: 'AM4' },
      ]
    }
  };

  const ramSizes = [8, 16, 32, 64, 128];
  const ramFreqsDDR4 = [2133, 2400, 2666, 2933, 3000, 3200, 3600, 3800, 4000, 4400, 4800];
  const ramFreqsDDR5 = [4800, 5200, 5600, 6000, 6400, 6800, 7200, 7600, 8000, 8400, 8800];

  const tierConfig = {
    flagship: { 
      label: language === 'FR' ? '★ Flagship' : '★ Flagship', 
      color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50' 
    },
    high: { 
      label: language === 'FR' ? 'Haut de gamme' : 'High-end', 
      color: 'bg-green-500/20 text-green-400 border-green-500/50' 
    },
    mid: { 
      label: language === 'FR' ? 'Milieu de gamme' : 'Mid-range', 
      color: 'bg-blue-500/20 text-blue-400 border-blue-500/50' 
    },
    entry: { 
      label: language === 'FR' ? 'Entrée de gamme' : 'Entry-level', 
      color: 'bg-gray-500/20 text-gray-400 border-gray-500/50' 
    }
  };

  const calculateResults = () => {
    if (!selectedGpu || !selectedCpu) return null;
    
    const gpuScore = selectedGpu.score;
    const cpuScore = selectedCpu.score;
    const ramMultiplier = Math.min((ramSize / 16) * (ramType === 'DDR5' ? 1.15 : 1) * (ramFreq / 5500), 1.3);
    
    const baseScore = (gpuScore * 0.55 + cpuScore * 0.35 + ramMultiplier * 10);
    
    const gains = {
      avg: Math.round(baseScore * 1.5 + Math.random() * 20),
      low1: Math.round(baseScore * 0.9 + Math.random() * 15),
      peak: Math.round(baseScore * 2 + Math.random() * 30)
    };
    
    return gains;
  };

  // Animate gains on results page
  useEffect(() => {
    if (showResults) {
      const results = calculateResults();
      if (!results) return;

      const duration = 1500;
      const steps = 60;
      const stepDuration = duration / steps;
      let currentStep = 0;

      const interval = setInterval(() => {
        currentStep++;
        const progress = currentStep / steps;
        const easeProgress = 1 - Math.pow(1 - progress, 3);

        setAnimatedGains({
          avg: Math.round(results.avg * easeProgress),
          low1: Math.round(results.low1 * easeProgress),
          peak: Math.round(results.peak * easeProgress)
        });

        if (currentStep >= steps) {
          clearInterval(interval);
        }
      }, stepDuration);

      return () => clearInterval(interval);
    }
  }, [showResults, selectedGpu, selectedCpu, ramSize, ramType, ramFreq]);

  const handleBack = () => {
    if (showResults) {
      setShowResults(false);
    } else if (step === 3) {
      setStep(2);
    } else if (step === 2) {
      if (selectedCpu) {
        setSelectedCpu(null);
      } else {
        setCpuBrand(null);
        setStep(1);
      }
    } else if (step === 1) {
      if (selectedGpu) {
        setSelectedGpu(null);
      } else {
        setGpuBrand(null);
      }
    }
    setSearchQuery('');
  };

  const handleReset = () => {
    setStep(1);
    setGpuBrand(null);
    setCpuBrand(null);
    setSelectedGpu(null);
    setSelectedCpu(null);
    setRamType('DDR5');
    setRamSize(16);
    setRamFreq(6000);
    setShowResults(false);
    setSearchQuery('');
  };

  const handleGpuSelect = (gpu) => {
    setSelectedGpu(gpu);
    setStep(2);
    setSearchQuery('');
  };

  const handleCpuSelect = (cpu) => {
    setSelectedCpu(cpu);
    if (cpu.socket === 'AM5' || cpu.socket === 'LGA 1851') {
      setRamType('DDR5');
      setRamFreq(6000);
    } else if (cpu.socket === 'AM4' || cpu.socket === 'LGA 1200') {
      setRamType('DDR4');
      setRamFreq(3600);
    }
    setStep(3);
    setSearchQuery('');
  };

  const filterItems = (items) => {
    if (!searchQuery) return items;
    return items.filter(item => 
      item.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  const renderBrandSelection = (type) => {
    const isGpu = type === 'gpu';
    const title = isGpu 
      ? (language === 'FR' ? 'Sélectionne ta carte graphique' : 'Select your graphics card')
      : (language === 'FR' ? 'Sélectionne ton processeur' : 'Select your processor');
    const icon = isGpu ? <Monitor className="w-6 h-6" /> : <Cpu className="w-6 h-6" />;

    return (
      <div className="flex flex-col items-center animate-fadeInUp">
        {/* Step indicator with icon */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-cyan-600/20 border border-cyan-500/50 flex items-center justify-center text-cyan-400">
            {icon}
          </div>
          <span className="text-cyan-400 text-sm font-semibold tracking-wider uppercase">
            {language === 'FR' ? `Étape ${step} sur 3` : `Step ${step} of 3`}
          </span>
        </div>
        
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 text-center">{title}</h1>
        <p className="text-gray-400 mb-16 text-center max-w-md">
          {isGpu 
            ? (language === 'FR' ? 'Choisis le fabricant de ta carte graphique' : 'Choose your graphics card manufacturer')
            : (language === 'FR' ? 'Choisis le fabricant de ton processeur' : 'Choose your processor manufacturer')
          }
        </p>
        
        <div className="flex flex-col md:flex-row gap-8">
          {isGpu ? (
            <>
              {/* NVIDIA Card */}
              <button
                onClick={() => setGpuBrand('nvidia')}
                data-testid="nvidia-gpu-btn"
                className="group relative w-80 h-56 rounded-2xl bg-gradient-to-br from-[#76b900]/10 to-[#76b900]/5 border-2 border-[#76b900]/30 hover:border-[#76b900] transition-all duration-300 hover:scale-105 hover:shadow-[0_0_40px_rgba(118,185,0,0.3)] flex flex-col items-center justify-center overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-t from-[#76b900]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="mb-4 flex items-center gap-2">
                  <span className="text-[#76b900] text-5xl font-black tracking-tight" style={{fontFamily: 'system-ui'}}>NVIDIA</span>
                </div>
                <span className="text-[#76b900] text-sm mt-2 font-medium">GeForce RTX / GTX</span>
                <div className="absolute bottom-4 right-4 w-8 h-8 rounded-full bg-[#76b900]/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <ChevronRight className="w-5 h-5 text-[#76b900]" />
                </div>
              </button>

              {/* AMD Card */}
              <button
                onClick={() => setGpuBrand('amd')}
                data-testid="amd-gpu-btn"
                className="group relative w-80 h-56 rounded-2xl bg-gradient-to-br from-cyan-950/50 to-cyan-900/20 border-2 border-cyan-700/30 hover:border-cyan-500 transition-all duration-300 hover:scale-105 hover:shadow-[0_0_40px_rgba(239,68,68,0.3)] flex flex-col items-center justify-center overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-t from-cyan-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="mb-4 flex items-center gap-1">
                  <span className="text-white text-5xl font-black tracking-tight" style={{fontFamily: 'system-ui'}}>AMD</span>
                </div>
                <span className="text-cyan-400 text-sm mt-2 font-medium">Radeon RX</span>
                <div className="absolute bottom-4 right-4 w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <ChevronRight className="w-5 h-5 text-cyan-400" />
                </div>
              </button>
            </>
          ) : (
            <>
              {/* Intel Card */}
              <button
                onClick={() => setCpuBrand('intel')}
                data-testid="intel-cpu-btn"
                className="group relative w-80 h-56 rounded-2xl bg-gradient-to-br from-[#0071c5]/10 to-[#0071c5]/5 border-2 border-[#0071c5]/30 hover:border-[#0071c5] transition-all duration-300 hover:scale-105 hover:shadow-[0_0_40px_rgba(0,113,197,0.3)] flex flex-col items-center justify-center overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-t from-[#0071c5]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="mb-4 flex items-center">
                  <span className="text-[#0071c5] text-5xl font-black tracking-tight italic" style={{fontFamily: 'system-ui'}}>intel</span>
                </div>
                <span className="text-[#0071c5] text-sm mt-2 font-medium">Core / Core Ultra</span>
                <div className="absolute bottom-4 right-4 w-8 h-8 rounded-full bg-[#0071c5]/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <ChevronRight className="w-5 h-5 text-[#0071c5]" />
                </div>
              </button>

              {/* AMD Card */}
              <button
                onClick={() => setCpuBrand('amd')}
                data-testid="amd-cpu-btn"
                className="group relative w-80 h-56 rounded-2xl bg-gradient-to-br from-cyan-950/50 to-cyan-900/20 border-2 border-cyan-700/30 hover:border-cyan-500 transition-all duration-300 hover:scale-105 hover:shadow-[0_0_40px_rgba(239,68,68,0.3)] flex flex-col items-center justify-center overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-t from-cyan-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="mb-4 flex items-center gap-1">
                  <span className="text-white text-5xl font-black tracking-tight" style={{fontFamily: 'system-ui'}}>AMD</span>
                </div>
                <span className="text-cyan-400 text-sm mt-2 font-medium">Ryzen</span>
                <div className="absolute bottom-4 right-4 w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <ChevronRight className="w-5 h-5 text-cyan-400" />
                </div>
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderModelList = (type) => {
    const isGpu = type === 'gpu';
    const brand = isGpu ? gpuBrand : cpuBrand;
    const data = isGpu ? gpuData[brand] : cpuData[brand];
    const filteredItems = filterItems(data.items);
    const brandColor = brand === 'nvidia' ? '#76b900' : brand === 'intel' ? '#0071c5' : '#ef4444';

    return (
      <div className="flex flex-col items-center animate-fadeInUp max-w-3xl mx-auto w-full">
        {/* Header with brand indicator */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${brandColor}20`, borderColor: `${brandColor}50`, borderWidth: '1px' }}>
            {isGpu ? <Monitor className="w-5 h-5" style={{ color: brandColor }} /> : <Cpu className="w-5 h-5" style={{ color: brandColor }} />}
          </div>
          <span className="text-sm font-semibold tracking-wider uppercase" style={{ color: brandColor }}>
            {language === 'FR' ? `Étape ${step} sur 3` : `Step ${step} of 3`}
          </span>
        </div>
        
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-3 text-center">
          {language === 'FR' ? `Choisissez votre ${data.name}` : `Choose your ${data.name}`}
        </h1>
        <p className="text-gray-400 text-sm mb-10">
          {data.logo} • {data.items.length} {language === 'FR' ? 'modèles disponibles' : 'models available'}
        </p>
        
        <div className="w-full bg-zinc-900/70 border border-cyan-900/30 rounded-2xl p-8">
          {/* Search bar */}
          <div className="relative mb-6">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={language === 'FR' ? 'Rechercher un modèle...' : 'Search a model...'}
              data-testid="model-search-input"
              className="w-full bg-black/50 border border-cyan-900/40 rounded-xl px-5 py-4 text-white placeholder:text-gray-500 focus:border-cyan-500 focus:outline-none transition-colors text-lg"
            />
          </div>
          
          {/* Model list */}
          <div className="max-h-[450px] overflow-y-auto custom-scrollbar space-y-3 pr-2">
            {filteredItems.map((item, index) => (
              <button
                key={index}
                onClick={() => isGpu ? handleGpuSelect(item) : handleCpuSelect(item)}
                data-testid={`model-${item.name.replace(/\s+/g, '-').toLowerCase()}`}
                className="w-full flex items-center justify-between px-5 py-4 rounded-xl bg-black/40 border border-zinc-800 hover:border-cyan-500/60 hover:bg-cyan-950/20 transition-all duration-200 group"
              >
                <div className="flex items-center gap-4">
                  <span className="text-white font-medium text-base">{item.name}</span>
                  {!isGpu && item.socket && (
                    <span className="text-xs text-gray-500 bg-zinc-800 px-2 py-1 rounded">{item.socket}</span>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  <span className={`px-4 py-1.5 rounded-full text-xs font-semibold border ${tierConfig[item.tier].color}`}>
                    {tierConfig[item.tier].label}
                  </span>
                  <ChevronRight className="w-5 h-5 text-gray-600 group-hover:text-cyan-400 group-hover:translate-x-1 transition-all" />
                </div>
              </button>
            ))}
            {filteredItems.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">
                  {language === 'FR' ? 'Aucun résultat trouvé' : 'No results found'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderRamConfig = () => {
    const socket = selectedCpu?.socket || 'AM5';
    const ramFreqs = ramType === 'DDR5' ? ramFreqsDDR5 : ramFreqsDDR4;
    
    return (
      <div className="flex flex-col items-center animate-fadeInUp max-w-2xl mx-auto w-full">
        {/* Header */}
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-full bg-cyan-600/20 border border-cyan-500/50 flex items-center justify-center text-cyan-400">
            <MemoryStick className="w-4 h-4" />
          </div>
          <span className="text-cyan-400 text-xs font-semibold tracking-wider uppercase">
            {language === 'FR' ? 'Étape 3 sur 3' : 'Step 3 of 3'}
          </span>
        </div>
        
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-2 text-center">
          {language === 'FR' ? 'Configure ta RAM' : 'Configure your RAM'}
        </h1>
        <p className="text-gray-400 text-sm mb-6">
          Socket: <span className="text-cyan-400 font-semibold">{socket}</span>
        </p>
        
        <div className="w-full bg-zinc-900/70 border border-cyan-900/30 rounded-xl p-6">
          {/* RAM Type */}
          <div className="mb-6">
            <h3 className="text-white font-semibold mb-3 text-sm">{language === 'FR' ? 'Type de RAM' : 'RAM Type'}</h3>
            <div className="flex gap-3">
              {['DDR4', 'DDR5'].map((type) => (
                <button
                  key={type}
                  onClick={() => {
                    setRamType(type);
                    setRamFreq(type === 'DDR5' ? 6000 : 3600);
                  }}
                  data-testid={`ram-type-${type.toLowerCase()}`}
                  className={`flex-1 py-3 rounded-lg font-bold text-sm transition-all duration-300 ${
                    ramType === type
                      ? 'bg-gradient-to-r from-cyan-600 to-cyan-700 text-white shadow-[0_0_20px_rgba(239,68,68,0.4)]'
                      : 'bg-black/50 text-gray-400 hover:bg-zinc-800 hover:text-white border border-zinc-700'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          {/* RAM Size */}
          <div className="mb-6">
            <h3 className="text-white font-semibold mb-3 text-sm">{language === 'FR' ? 'Quantité (Go)' : 'Capacity (GB)'}</h3>
            <div className="flex gap-2">
              {ramSizes.map((size) => (
                <button
                  key={size}
                  onClick={() => setRamSize(size)}
                  data-testid={`ram-size-${size}`}
                  className={`flex-1 py-2.5 rounded-lg font-semibold text-sm transition-all duration-300 ${
                    ramSize === size
                      ? 'bg-cyan-600 text-white shadow-[0_0_15px_rgba(239,68,68,0.3)]'
                      : 'bg-black/50 text-gray-400 hover:bg-zinc-800 hover:text-white border border-zinc-700'
                  }`}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>

          {/* RAM Frequency */}
          <div className="mb-6">
            <h3 className="text-white font-semibold mb-3 text-sm">{language === 'FR' ? 'Fréquence (MHz)' : 'Frequency (MHz)'}</h3>
            <div className="grid grid-cols-4 gap-2">
              {ramFreqs.map((freq) => (
                <button
                  key={freq}
                  onClick={() => setRamFreq(freq)}
                  data-testid={`ram-freq-${freq}`}
                  className={`py-2.5 rounded-lg font-medium text-sm transition-all duration-300 ${
                    ramFreq === freq
                      ? 'bg-cyan-600 text-white shadow-[0_0_12px_rgba(239,68,68,0.3)]'
                      : 'bg-black/50 text-gray-400 hover:bg-zinc-800 hover:text-white border border-zinc-700'
                  }`}
                >
                  {freq}
                </button>
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <Button 
            onClick={() => setShowResults(true)}
            data-testid="see-results-btn"
            className="w-full bg-gradient-to-r from-cyan-600 to-cyan-700 hover:from-cyan-500 hover:to-cyan-600 text-white font-bold py-4 rounded-lg transition-all duration-300 text-base shadow-[0_0_25px_rgba(6,182,212,0.4)] hover:shadow-[0_0_35px_rgba(6,182,212,0.6)]"
          >
            <Zap className="w-4 h-4 mr-2" />
            {language === 'FR' ? 'Voir mes gains estimés' : 'See my estimated gains'}
          </Button>
        </div>
      </div>
    );
  };

  const renderResults = () => {
    const cpuTier = selectedCpu?.tier || 'mid';
    const gpuTier = selectedGpu?.tier || 'mid';

    return (
      <div className="flex flex-col items-center animate-fadeInUp max-w-3xl mx-auto w-full" data-testid="results-section">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
            {language === 'FR' ? 'Tes gains estimés' : 'Your estimated gains'}
          </h1>
          <p className="text-cyan-400 flex items-center justify-center gap-2 text-sm">
            <Gamepad2 className="w-4 h-4" />
            {language === 'FR' ? 'en jeu sur Fortnite' : 'in-game on Fortnite'}
          </p>
        </div>

        {/* Configuration Summary */}
        <div className="w-full bg-zinc-900/80 border border-cyan-900/30 rounded-xl p-4 mb-6">
          <h3 className="text-white font-semibold mb-3 text-sm">
            {language === 'FR' ? 'Ta configuration' : 'Your configuration'}
          </h3>
          <div className="flex flex-wrap gap-2">
            <div className="flex items-center gap-2 bg-black/40 px-3 py-1.5 rounded-lg border border-zinc-800">
              <Cpu className="w-3 h-3 text-cyan-400" />
              <span className="text-gray-300 text-sm">{selectedCpu?.name}</span>
            </div>
            <div className="flex items-center gap-2 bg-black/40 px-3 py-1.5 rounded-lg border border-zinc-800">
              <Monitor className="w-3 h-3 text-cyan-400" />
              <span className="text-gray-300 text-sm">{selectedGpu?.name}</span>
            </div>
            <div className="flex items-center gap-2 bg-black/40 px-3 py-1.5 rounded-lg border border-zinc-800">
              <MemoryStick className="w-3 h-3 text-cyan-400" />
              <span className="text-gray-300 text-sm">{ramSize}Go {ramType} {ramFreq}MHz</span>
            </div>
          </div>
        </div>

        {/* Gains Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full mb-6">
          {/* Average FPS Gain - Featured */}
          <div className="bg-gradient-to-br from-cyan-950/60 to-cyan-900/30 border-2 border-cyan-500/50 rounded-xl p-5 text-center relative overflow-hidden md:transform md:scale-105">
            <div className="absolute inset-0 bg-gradient-to-t from-cyan-500/10 to-transparent"></div>
            <div className="absolute top-2 right-2">
              <span className="bg-cyan-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">PRINCIPAL</span>
            </div>
            <p className="text-cyan-300/80 text-xs mb-1 relative z-10 mt-2">
              {language === 'FR' ? 'Gain FPS moyen estimé' : 'Estimated avg FPS gain'}
            </p>
            <p className="text-4xl font-bold text-cyan-400 mb-1 relative z-10" data-testid="avg-fps-gain">
              +{animatedGains.avg}
            </p>
            <p className="text-cyan-400/80 text-sm relative z-10">FPS</p>
          </div>

          {/* 1% Low */}
          <div className="bg-zinc-900/80 border border-zinc-700/50 rounded-xl p-5 text-center">
            <p className="text-gray-400 text-xs mb-1">1% Low (minimum)</p>
            <p className="text-3xl font-bold text-white mb-1" data-testid="low1-fps-gain">+{animatedGains.low1}</p>
            <p className="text-gray-500 text-sm">FPS</p>
          </div>

          {/* Peak Performance */}
          <div className="bg-zinc-900/80 border border-zinc-700/50 rounded-xl p-5 text-center">
            <p className="text-gray-400 text-xs mb-1">
              {language === 'FR' ? 'Pics de performance' : 'Performance peaks'}
            </p>
            <p className="text-3xl font-bold text-cyan-300 mb-1" data-testid="peak-fps-gain">+{animatedGains.peak}</p>
            <p className="text-gray-500 text-sm">FPS</p>
          </div>
        </div>

        {/* Recommendations */}
        <div className="w-full bg-zinc-900/80 border border-zinc-700/50 rounded-xl p-5 mb-5">
          <h3 className="text-white font-semibold flex items-center gap-2 mb-4 text-sm">
            <Zap className="w-4 h-4 text-cyan-400" />
            {language === 'FR' ? 'Recommandations personnalisées' : 'Personalized recommendations'}
          </h3>
          
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-400 flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
              <div className="flex items-center gap-2">
                <Cpu className="w-4 h-4 text-orange-400 flex-shrink-0" />
                <span className="text-gray-300 text-sm">
                  {cpuTier === 'flagship' 
                    ? (language === 'FR' ? 'CPU flagship - Optimisations BIOS et latences pour gains marginaux mais perceptibles' : 'Flagship CPU - BIOS and latency optimizations for marginal but noticeable gains')
                    : (language === 'FR' ? 'CPU milieu de gamme - Optimisations système et latences pour gains importants' : 'Mid-range CPU - System and latency optimizations for significant gains')
                  }
                </span>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-400 flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
              <div className="flex items-center gap-2">
                <Monitor className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                <span className="text-gray-300 text-sm">
                  {gpuTier === 'flagship' || gpuTier === 'high'
                    ? (language === 'FR' ? 'GPU haut de gamme - Focus sur réduction input lag et frame pacing' : 'High-end GPU - Focus on input lag reduction and frame pacing')
                    : (language === 'FR' ? 'GPU - Optimisation des pilotes et paramètres pour performances maximales' : 'GPU - Driver optimization and settings for maximum performance')
                  }
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="w-full bg-amber-950/30 border border-amber-700/40 rounded-lg p-4 mb-6 flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-amber-200/80 text-xs leading-relaxed">
            {language === 'FR' 
              ? `Ces résultats sont des estimations basées sur notre benchmark de référence (Ryzen 5 7600X + RTX 3060 + DDR5 6000 CL30). Les gains réels peuvent varier selon ta configuration spécifique.`
              : `These results are estimates based on our reference benchmark (Ryzen 5 7600X + RTX 3060 + DDR5 6000 CL30). Actual gains may vary depending on your specific configuration.`
            }
          </p>
        </div>

        {/* Reset Button */}
        <button
          onClick={handleReset}
          data-testid="reset-btn"
          className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-zinc-800/80 border border-zinc-700 text-gray-300 hover:text-white hover:border-cyan-500/50 transition-all duration-300 text-sm"
        >
          <RotateCcw className="w-4 h-4" />
          {language === 'FR' ? 'Recommencer' : 'Start over'}
        </button>
      </div>
    );
  };

  const renderContent = () => {
    if (showResults) return renderResults();
    
    if (step === 1) {
      if (!gpuBrand) return renderBrandSelection('gpu');
      return renderModelList('gpu');
    }
    
    if (step === 2) {
      if (!cpuBrand) return renderBrandSelection('cpu');
      return renderModelList('cpu');
    }
    
    if (step === 3) {
      return renderRamConfig();
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-20 px-8">
      {/* Custom scrollbar styles */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.3);
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(239, 68, 68, 0.5);
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(239, 68, 68, 0.7);
        }
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeInUp {
          animation: fadeInUp 0.5s ease-out forwards;
        }
      `}</style>
      
      <div className="max-w-5xl mx-auto">
        {/* Back button */}
        {(gpuBrand || step > 1) && !showResults && (
          <button
            onClick={handleBack}
            data-testid="back-btn"
            className="flex items-center gap-2 text-gray-400 hover:text-cyan-400 transition-colors mb-10 group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            {language === 'FR' ? 'Retour' : 'Back'}
          </button>
        )}

        {renderContent()}
      </div>
    </div>
  );
};

export default FPSEstimatorPage;
